import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  archiveBusinessService,
  createBusinessService,
  listBusinessServices,
  updateBusinessService,
} from './services-api';
import type { BusinessService, UpsertBusinessServicePayload } from './types';

export const businessServiceKeys = {
  all: ['business-services'] as const,
  list: () => [...businessServiceKeys.all, 'list'] as const,
};

export function useBusinessServices() {
  return useQuery({
    queryKey: businessServiceKeys.list(),
    queryFn: listBusinessServices,
    staleTime: 30_000,
  });
}

export function useCreateBusinessService() {
  const qc = useQueryClient();
  return useMutation<BusinessService, Error, UpsertBusinessServicePayload>({
    mutationFn: createBusinessService,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: businessServiceKeys.all });
    },
  });
}

export function useUpdateBusinessService() {
  const qc = useQueryClient();
  return useMutation<
    BusinessService,
    Error,
    { serviceId: string; payload: Partial<UpsertBusinessServicePayload> }
  >({
    mutationFn: ({ serviceId, payload }) => updateBusinessService(serviceId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: businessServiceKeys.all });
    },
  });
}

export function useArchiveBusinessService() {
  const qc = useQueryClient();
  return useMutation<void, Error, { serviceId: string; reason: string }>({
    mutationFn: ({ serviceId, reason }) => archiveBusinessService(serviceId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: businessServiceKeys.all });
    },
  });
}
